/**
 * Which way the catalog opens, on this device (FR-3.1a).
 *
 * FR-3.1 gives the catalog two renderings and the toolbar switches between them,
 * but the switch lasted as long as the screen did: every visit started on the
 * list again. For someone who photographs what they catalogue that is a tap
 * before every browse, paid forever, to undo a default chosen for someone else.
 *
 * A default rather than a memory. The toolbar still switches freely and nothing
 * it does is written down -- what is stored is the answer to "which one should
 * this device start on", asked once in Settings. The alternative, persisting
 * whatever the toolbar last did, decides that question by accident: a single
 * glance at the grid would silently change what every later visit opens on, and
 * there would be nowhere to say otherwise.
 *
 * Kept on the device, like the theme and the currency display and for the same
 * reason: it is a fact about a screen rather than about the manifest. A phone is
 * held at arm's length and a laptop is not, which is most of what decides
 * whether photographs or rows are the better way in -- syncing one answer to
 * both would be a bug wearing the clothes of a feature. Losing it costs a
 * preference and no data.
 *
 * `list` remains the fallback, for the reasons `Catalog.tsx` gives where it
 * reads this: it carries the per-item controls, and a catalog is usually written
 * down before it is photographed.
 */
export type CatalogView = 'grid' | 'list';

/**
 * The choices, in the order they are offered.
 *
 * List first, because it is the default and the order should not imply that the
 * app would rather you picked the other one.
 */
export const CATALOG_VIEWS: readonly { value: CatalogView; label: string }[] = [
  { value: 'list', label: 'List' },
  { value: 'grid', label: 'Grid' },
];

/**
 * What each one is for, said once.
 *
 * A record rather than a field on the list above, so that reading the hint for
 * the current choice is an index rather than a search that can come back empty
 * -- there is no such thing as a `CatalogView` without one.
 */
export const CATALOG_VIEW_HINTS: Readonly<Record<CatalogView, string>> = {
  list: 'Compact rows, with the controls for each item.',
  grid: 'Photographs, which identify a thing faster than its name.',
};

const KEY = 'intentionalist.catalog-view';

const isView = (value: string | null): value is CatalogView => value === 'grid' || value === 'list';

/**
 * What this device has chosen, or `list`.
 *
 * An unreadable or unrecognized value falls back rather than throwing. It can
 * come from storage the browser has denied, from private browsing, or from a
 * build that spelled the values differently, and none of those deserves a blank
 * catalog over a layout.
 */
export function storedCatalogView(): CatalogView {
  try {
    const stored = localStorage.getItem(KEY);
    return isView(stored) ? stored : 'list';
  } catch {
    return 'list';
  }
}

/** Records the choice on this device. */
export function setCatalogView(view: CatalogView): void {
  try {
    localStorage.setItem(KEY, view);
  } catch {
    // Storage denied. Nothing to apply here -- unlike the theme, this is read
    // when the catalog mounts rather than held on the document -- so the choice
    // simply does not outlive the session.
  }
}

/** Testing seam only. */
export function resetCatalogView(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing depends on this having worked.
  }
}
